<template>
    <div
        class="position-relative flex-fill overflow-auto px-3"
        :class="{ 'comments-list-loading-empty': loading && !comments.length }">
        <ELoader v-if="loading" class="overlay-loader"/>
        <div
            v-for="(comment, index) in comments"
            :key="comment.id"
            :data-comment-id="comment.id"
            class="comments-list-row d-flex py-2 position-relative"
            :class="{ 'border-top': index > 0 }">
            <ELoader v-if="loadingCommentId === comment.id" class="overlay-loader"/>
            <div class="d-flex flex-column w-100">
                <div class="d-flex align-items-center mb-1">
                    <div class="d-flex align-items-center">
                        <ProfileImage
                            v-if="comment.creator"
                            :image-url="comment.creator.profile_picture"
                            :name="comment.creator.name"
                            class="goal-arrow-spacer"
                            size="sm"/>
                        <div class="d-flex flex-column mr-2">
                            <span v-if="comment.creator" class="font-weight-bold">{{ comment.creator.name }}</span>
                            <small class="text-muted">{{ formatDateTime(comment.created_at) }}</small>
                        </div>
                    </div>
                    <!-- TODO: Vue3 $scopedSlots is $slots -->
                    <div v-if="$scopedSlots.actions || showResolve || showDelete" class="d-flex ml-auto flex-shrink-0">
                        <slot name="actions" :comment="comment" :loading="isAnyActionBusy" :can-delete="canDelete(comment)">
                            <RoundIconButton
                                v-if="showResolve"
                                type="btn-success"
                                :disabled="isAnyActionBusy"
                                :title="$t('Mark comment as resolved')"
                                @click="confirmResolve(comment)">
                                <CheckIcon size="18" class="text-white"/>
                            </RoundIconButton>
                            <RoundIconButton
                                v-if="showDelete && canDelete(comment)"
                                class="ml-1"
                                type="bg-danger"
                                :disabled="isAnyActionBusy"
                                :title="$t('Delete')"
                                @click="confirmDelete(comment)">
                                <TrashCanIcon size="18" class="text-white"/>
                            </RoundIconButton>
                        </slot>
                    </div>
                </div>
                <div class="d-flex">
                    <div class="goal-arrow-spacer"/>
                    <div class="text-content text-break" v-html="comment.comment"/>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { formatDateTime } from '@/filters';
import { eventBus } from '@/main';
import CheckIcon from '@/common/components/svg-icons/CheckIcon.vue';
import TrashCanIcon from '@/common/components/svg-icons/TrashCanIcon.vue';

export default {
    name: 'CommentsList',
    props: {
        comments: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        loadingCommentId: {
            type: Number,
            default: null
        },
        showResolve: {
            type: Boolean,
            default: false
        },
        showDelete: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            pendingConfirm: null
        };
    },
    computed: {
        ...mapGetters(['currentEmployee']),
        currentEmployeeId() {
            return this.currentEmployee?.id;
        },
        isAnyActionBusy() {
            return this.loading || !!this.loadingCommentId;
        }
    },
    methods: {
        formatDateTime, // TODO: Vue3 remove
        canDelete(comment) {
            return comment.created_by === this.currentEmployeeId;
        },
        confirmResolve(comment) {
            if (this.isAnyActionBusy) {
                return;
            }
            this.pendingConfirm = { comment, action: 'resolve' };
            eventBus.$emit('show-action-confirmation', {
                uid: this._uid,
                color: 'success',
                msg: this.$t('Are you sure you want to resolve this comment?'),
                confirmHeader: this.$t('Mark comment as resolved'),
                confirmText: this.$t('Resolve')
            });
        },
        confirmDelete(comment) {
            if (this.isAnyActionBusy) {
                return;
            }
            this.pendingConfirm = { comment, action: 'delete' };
            eventBus.$emit('show-action-confirmation', {
                uid: this._uid,
                color: 'danger',
                msg: this.$t('Are you sure you want to delete this comment?'),
                confirmHeader: this.$t('Delete comment'),
                confirmText: this.$t('Delete')
            });
        },
        onActionConfirmed({ uid }) {
            if (uid !== this._uid || !this.pendingConfirm) {
                return;
            }
            const { comment, action } = this.pendingConfirm;
            this.pendingConfirm = null;
            this.$emit(action, comment);
        },
        onActionCancelled(uid) {
            if (uid === this._uid) {
                this.pendingConfirm = null;
            }
        },
        scrollCommentIntoView(commentId) {
            this.$nextTick(() => this.$el.querySelector(`[data-comment-id="${commentId}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
        }
    },
    components: {
        CheckIcon,
        TrashCanIcon
    },
    mounted() {
        eventBus.$on('action-confirmed', this.onActionConfirmed);
        eventBus.$on('action-cancelled', this.onActionCancelled);
    },
    beforeDestroy() {  // Vue 3 Rename beforeUnmount
        eventBus.$off('action-confirmed', this.onActionConfirmed);
        eventBus.$off('action-cancelled', this.onActionCancelled);
    }
};
</script>

<style scoped>
.comments-list-loading-empty,
.comments-list-row:has(> .overlay-loader) > .d-flex.flex-column {
    min-height: 3rem;
    opacity: 0.3;
    transition: opacity 0.2s ease-in;
}
</style>
